import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

/** Shape persisted by legacy `AuthContext` (localStorage `googleUser`). */
export interface GoogleUserStored {
  uid?: string
  email?: string
  displayName?: string
  photoURL?: string
  accessToken?: string
  role?: string
}

export interface UserProfileOverrides {
  /** Overrides Google display name in v2 UI when set. */
  preferredName: string
  jobTitle: string
  phone: string
  timezone: string
}

export interface AdminWorkspacePreferences {
  defaultReportingCurrency: string
  fiscalYearStartMonth: number
  dateFormat: 'mdy' | 'dmy' | 'ymd'
}

export interface AdminFeatureToggles {
  showBetaNav: boolean
  compactTables: boolean
  exportWatermark: boolean
}

export interface AdminNotificationPreferences {
  weeklyDigestEmail: boolean
  productUpdateEmails: boolean
}

export interface AuditLogEntry {
  id: string
  at: string
  action: string
  detail?: string
}

const LS_GOOGLE = 'googleUser'
const LS_PROFILE = 'v2-user-profile'
const LS_ADMIN_PREFS = 'v2-admin-preferences'
const LS_AUDIT = 'v2-audit-log'
const AUDIT_MAX = 40

const defaultProfile: UserProfileOverrides = {
  preferredName: '',
  jobTitle: '',
  phone: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
}

const defaultWorkspace: AdminWorkspacePreferences = {
  defaultReportingCurrency: 'INR',
  fiscalYearStartMonth: 4,
  dateFormat: 'dmy',
}

const defaultFeatures: AdminFeatureToggles = {
  showBetaNav: true,
  compactTables: false,
  exportWatermark: false,
}

const defaultNotifications: AdminNotificationPreferences = {
  weeklyDigestEmail: false,
  productUpdateEmails: true,
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    return { ...fallback, ...JSON.parse(raw) } as T
  } catch {
    return fallback
  }
}

function readGoogleUser(): GoogleUserStored | null {
  try {
    const raw = localStorage.getItem(LS_GOOGLE)
    if (!raw) return null
    const u = JSON.parse(raw) as GoogleUserStored
    return u?.email || u?.displayName ? u : null
  } catch {
    return null
  }
}

function readAudit(): AuditLogEntry[] {
  try {
    const raw = localStorage.getItem(LS_AUDIT)
    if (!raw) return []
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? arr : []
  } catch {
    return []
  }
}

interface UserSessionContextValue {
  googleUser: GoogleUserStored | null
  isSignedIn: boolean
  isAdmin: boolean
  roleLabel: string
  profile: UserProfileOverrides
  setProfile: (p: Partial<UserProfileOverrides>) => void
  workspace: AdminWorkspacePreferences
  setWorkspace: (p: Partial<AdminWorkspacePreferences>) => void
  features: AdminFeatureToggles
  setFeatures: (p: Partial<AdminFeatureToggles>) => void
  notifications: AdminNotificationPreferences
  setNotifications: (p: Partial<AdminNotificationPreferences>) => void
  auditLog: AuditLogEntry[]
  logAudit: (action: string, detail?: string) => void
  clearAuditLog: () => void
  displayName: string
  emailLine: string
  initials: string
  refreshSession: () => void
  signOut: () => void
}

const UserSessionContext = createContext<UserSessionContextValue | null>(null)

function roleDisplay(role?: string): string {
  const r = (role || '').toLowerCase()
  if (r === 'admin') return 'Admin'
  if (r === 'rmg') return 'RMG'
  if (r === 'finance') return 'Finance'
  return r ? r : 'User'
}

function initialsFrom(name: string, email: string): string {
  const n = name.trim()
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return n.slice(0, 2).toUpperCase()
  }
  const e = email.trim()
  if (e) return e.slice(0, 2).toUpperCase()
  return '??'
}

export function UserSessionProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [googleUser, setGoogleUser] = useState<GoogleUserStored | null>(() =>
    readGoogleUser()
  )
  const [isAdmin, setIsAdmin] = useState(false)
  const [profile, setProfileState] = useState<UserProfileOverrides>(() =>
    readJson(LS_PROFILE, defaultProfile)
  )
  const [workspace, setWorkspaceState] = useState<AdminWorkspacePreferences>(
    () => readJson(LS_ADMIN_PREFS + ':workspace', defaultWorkspace)
  )
  const [features, setFeaturesState] = useState<AdminFeatureToggles>(() =>
    readJson(LS_ADMIN_PREFS + ':features', defaultFeatures)
  )
  const [notifications, setNotificationsState] =
    useState<AdminNotificationPreferences>(() =>
      readJson(LS_ADMIN_PREFS + ':notifications', defaultNotifications)
    )
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>(() => readAudit())

  const refreshSession = useCallback(() => {
    setGoogleUser(readGoogleUser())
    setAuditLog(readAudit())
  }, [])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_GOOGLE || e.key === null) refreshSession()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [refreshSession])

  useEffect(() => {
    const onFocus = () => refreshSession()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refreshSession])

  useEffect(() => {
    const email = (googleUser?.email || '').trim().toLowerCase()
    const roleAdmin = (googleUser?.role || '').toLowerCase() === 'admin'
    if (roleAdmin) {
      setIsAdmin(true)
      return
    }
    if (!email) {
      setIsAdmin(false)
      return
    }
    let cancelled = false
    fetch('/api/admin-emails')
      .then(res => res.json().catch(() => ({ emails: [] })))
      .then(data => {
        if (cancelled) return
        const list = Array.isArray(data?.emails) ? data.emails : []
        const norm = (x: string) => String(x).trim().toLowerCase()
        setIsAdmin(list.some((x: string) => norm(x) === email))
      })
      .catch(() => {
        if (!cancelled) setIsAdmin(false)
      })
    return () => {
      cancelled = true
    }
  }, [googleUser?.email, googleUser?.role])

  const persistProfile = useCallback((next: UserProfileOverrides) => {
    localStorage.setItem(LS_PROFILE, JSON.stringify(next))
    setProfileState(next)
  }, [])

  const setProfile = useCallback(
    (patch: Partial<UserProfileOverrides>) => {
      persistProfile({ ...profile, ...patch })
    },
    [profile, persistProfile]
  )

  const setWorkspace = useCallback(
    (patch: Partial<AdminWorkspacePreferences>) => {
      const next = { ...workspace, ...patch }
      localStorage.setItem(
        LS_ADMIN_PREFS + ':workspace',
        JSON.stringify(next)
      )
      setWorkspaceState(next)
    },
    [workspace]
  )

  const setFeatures = useCallback(
    (patch: Partial<AdminFeatureToggles>) => {
      const next = { ...features, ...patch }
      localStorage.setItem(
        LS_ADMIN_PREFS + ':features',
        JSON.stringify(next)
      )
      setFeaturesState(next)
    },
    [features]
  )

  const setNotifications = useCallback(
    (patch: Partial<AdminNotificationPreferences>) => {
      const next = { ...notifications, ...patch }
      localStorage.setItem(
        LS_ADMIN_PREFS + ':notifications',
        JSON.stringify(next)
      )
      setNotificationsState(next)
    },
    [notifications]
  )

  const logAudit = useCallback((action: string, detail?: string) => {
    const entry: AuditLogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      at: new Date().toISOString(),
      action,
      detail,
    }
    setAuditLog(prev => {
      const next = [entry, ...prev].slice(0, AUDIT_MAX)
      localStorage.setItem(LS_AUDIT, JSON.stringify(next))
      return next
    })
  }, [])

  const clearAuditLog = useCallback(() => {
    const entry: AuditLogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      at: new Date().toISOString(),
      action: 'Audit log cleared',
      detail: 'Previous entries removed locally',
    }
    const next = [entry]
    localStorage.setItem(LS_AUDIT, JSON.stringify(next))
    setAuditLog(next)
  }, [])

  const signOut = useCallback(() => {
    logAudit('Signed out')
    localStorage.removeItem(LS_GOOGLE)
    setGoogleUser(null)
    window.location.href = '/'
  }, [logAudit])

  const displayName = useMemo(() => {
    const pref = profile.preferredName.trim()
    if (pref) return pref
    const g = googleUser?.displayName?.trim()
    if (g) return g
    return 'Guest'
  }, [profile.preferredName, googleUser?.displayName])

  const emailLine = useMemo(() => {
    return googleUser?.email?.trim() || 'Not signed in'
  }, [googleUser?.email])

  const initials = useMemo(
    () => initialsFrom(displayName, googleUser?.email || ''),
    [displayName, googleUser?.email]
  )

  const roleLabel = useMemo(
    () => roleDisplay(googleUser?.role),
    [googleUser?.role]
  )

  const value = useMemo<UserSessionContextValue>(
    () => ({
      googleUser,
      isSignedIn: !!googleUser?.email,
      isAdmin,
      roleLabel,
      profile,
      setProfile,
      workspace,
      setWorkspace,
      features,
      setFeatures,
      notifications,
      setNotifications,
      auditLog,
      logAudit,
      clearAuditLog,
      displayName,
      emailLine,
      initials,
      refreshSession,
      signOut,
    }),
    [
      googleUser,
      isAdmin,
      roleLabel,
      profile,
      setProfile,
      workspace,
      setWorkspace,
      features,
      setFeatures,
      notifications,
      setNotifications,
      auditLog,
      logAudit,
      clearAuditLog,
      displayName,
      emailLine,
      initials,
      refreshSession,
      signOut,
    ]
  )

  return (
    <UserSessionContext.Provider value={value}>
      {children}
    </UserSessionContext.Provider>
  )
}

export function useUserSession(): UserSessionContextValue {
  const ctx = useContext(UserSessionContext)
  if (!ctx) {
    throw new Error('useUserSession must be used within UserSessionProvider')
  }
  return ctx
}
